import { Money } from "@frontend/components/ui";
import type { OfferChange } from "@creators/backend/lib/collabs/diff";
import type { OfferView } from "@creators/backend/lib/collabs/queries";
import { advancePct, TRIGGER_LABELS } from "@creators/backend/lib/collabs/schedule";
import type { CollabStatus, Party } from "@creators/backend/lib/collabs/state-machine";
import { splitProportionally } from "@creators/backend/lib/money";
import { formatDate } from "@creators/backend/lib/time";

export function OfferCard({
  offer,
  changes,
  frozen,
}: {
  offer: OfferView;
  changes: OfferChange[];
  status: CollabStatus;
  pendingParty?: Party;
  viewer?: "creator" | "brand" | "admin";
  frozen: boolean;
  compact?: boolean;
}) {
  const isBarter = offer.compensationType === "barter";
  const advance = isBarter ? 0 : advancePct(offer.schedule);
  const amounts = isBarter
    ? []
    : splitProportionally(
        offer.amountTotal,
        offer.schedule.map((s) => s.pct),
      );
  const advanceAmount = advance
    ? amounts[offer.schedule.findIndex((s) => s.trigger === "on_accept")]
    : 0;
  const balance = offer.amountTotal - advanceAmount;

  return (
    <section className="card overflow-hidden">
      <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-2.5">
        <h2 className="text-sm font-semibold text-ink">Collab terms</h2>
        <div className="flex shrink-0 items-center gap-1.5">
          {offer.version > 1 ? <Tag>Countered</Tag> : null}
          <Tag>{frozen ? "Locked" : "Draft"}</Tag>
        </div>
      </div>

      <div className="px-4 py-3">
        {isBarter ? (
          <p className="text-base leading-snug font-semibold">{offer.barterDescription}</p>
        ) : (
          <Money paise={offer.amountTotal} size="xl" className="text-xl tracking-tight" />
        )}

        <p className="mt-1 line-clamp-2 text-sm leading-snug text-ink-muted">{offer.brief}</p>

        {changes.length > 0 && !frozen ? (
          <ul className="mt-3 flex flex-wrap gap-1.5">
            {changes.map((change) => (
              <li
                key={change.field}
                className="rounded-md bg-warn-50 px-2 py-0.5 text-[11px] text-warn-700 ring-1 ring-warn-100"
              >
                <span className="font-medium">{change.field}</span> {change.from} → {change.to}
              </li>
            ))}
          </ul>
        ) : null}

        <dl className="mt-3 space-y-1.5 border-t border-line pt-3 text-sm">
          <Row label="Deliver by">{formatDate(offer.deliverBy)}</Row>
          <Row label="Compensation">{isBarter ? "Barter" : "Paid directly"}</Row>
        </dl>

        {!isBarter ? (
          <details className="group mt-3 border-t border-line pt-2.5">
            <summary className="flex cursor-pointer list-none items-center justify-between text-xs font-medium text-ink-muted transition hover:text-ink [&::-webkit-details-marker]:hidden">
              Payment breakdown
              <span className="text-ink-subtle transition group-open:rotate-90">›</span>
            </summary>

            <div className="mt-2.5 space-y-2.5 rounded-lg bg-canvas/70 px-3 py-2.5 ring-1 ring-line/60">
              <dl className="grid grid-cols-2 gap-2 text-xs">
                <Term label="Advance">
                  {advance > 0 ? (
                    <>
                      {advance}% · <Money paise={advanceAmount} size="sm" />
                    </>
                  ) : (
                    "—"
                  )}
                </Term>
                <Term label="Balance">
                  <Money paise={balance} size="sm" />
                </Term>
              </dl>

              <ul className="space-y-1.5 border-t border-line pt-2 text-xs">
                {offer.schedule.map((item, index) => (
                  <li key={item.trigger} className="flex justify-between gap-2">
                    <span className="text-ink-muted">{TRIGGER_LABELS[item.trigger]}</span>
                    <span className="tabular font-medium">
                      {item.pct}% · <Money paise={amounts[index]} size="sm" />
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </details>
        ) : null}
      </div>
    </section>
  );
}

function Tag({ children }: { children: React.ReactNode }) {
  return (
    <span className="rounded bg-canvas px-1.5 py-0.5 text-[10px] font-medium tracking-wide text-ink-subtle uppercase">
      {children}
    </span>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-ink-muted">{label}</dt>
      <dd className="tabular text-right font-medium text-ink">{children}</dd>
    </div>
  );
}

function Term({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-[10px] tracking-wide text-ink-subtle uppercase">{label}</dt>
      <dd className="mt-0.5 font-medium text-ink">{children}</dd>
    </div>
  );
}
